/* eslint-disable react-hooks/rules-of-hooks */
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import Link from 'next/link';
import Image from 'next/image';

import { useTranslation } from '@hooks';
import { capitalizeSentences, truncateString } from '@utils';
import { ColorVariants, ProductCardSlider } from '@components/listing';
import { StyledRow } from '@components/styles/CommonStyles';
import { ProductFavorite } from '@components/products';
import { Caption2, Caption, Subtitle2 } from 'components/styles/Typography';
import Price from './Price';

const ProductCard = ({
    product,
    border,
    priority,
    listingType,
    isProdRequest,
}) => {
    const {
        name,
        images,
        price,
        variants,
        discounted_price: discountedPrice,
        discount_rate: discountRate,
    } = product;
    const { locale, useCurrency, t } = useTranslation();
    const [currentImage, setCurrentImage] = useState(
        product.images[0].urls.medium,
    );
    const [currentSlug, setCurrentSlug] = useState(product.slug);
    const [selectedVariant, setSelectedVariant] = useState(null);
    const changeProduct = (id) => {
        const newProduct = product.variants.find(
            (productItem) => productItem.id === id,
        );
        setCurrentImage(newProduct.images[0].urls.large);
        setCurrentSlug(newProduct.slug[locale]);
        setSelectedVariant(newProduct);
    };

    useEffect(() => {
        setCurrentImage(product.images[0].urls.large);
        setCurrentSlug(product.slug[locale]);
        setSelectedVariant(product);
    }, [product]);

    const route = isProdRequest
        ? `/${t('productSlug')}/${currentSlug}?production-request=true`
        : `/${t('productSlug')}/${currentSlug}`;
    return (
        <CardWrapper data-testid="product-link">
            {listingType.name !== 'octal' && (
                <ProductFavorite product={product} forListing />
            )}
            <Link href={route} passHref>
                <Card border={border} listingType={listingType}>
                    {listingType.name === 'octal' ? (
                        <ImageWrapper listingType={listingType}>
                            {listingType.name !== 'octal' && (
                                <ProductFavorite product={product} forListing />
                            )}
                            <Image
                                placeholder="blur"
                                blurDataURL={`/_next/image?url=${`/placeholder.webp`}&w=16&q=1`}
                                src={currentImage || images[0].urls.large}
                                alt={product.name[locale]}
                                object-fit="contain"
                                priority={priority}
                                quality={100}
                                fill
                            />
                        </ImageWrapper>
                    ) : (
                        <ProductCardSlider
                            product={selectedVariant || product}
                            priority={priority}
                            listingType={listingType}
                        />
                    )}
                    {listingType.name !== 'octal' && (
                        <CardContentWrapper>
                            <MobileCardContent>
                                <Price
                                    discountRate={discountRate}
                                    discountedPrice={useCurrency(
                                        discountedPrice,
                                    )}
                                    price={useCurrency(price)}
                                    letterSpacing="1px"
                                />

                                {product.collection_name === 'Plus Size' ? (
                                    <CollectionImage
                                        src="https://cloud.keikei.com/cdn/fervente/keikei_lovemycurves_3-removebg-preview.png"
                                        alt="plus size sticker"
                                        width="67px"
                                    />
                                ) : (
                                    product?.collection_name && (
                                        <CollectionMobile>
                                            {product.collection_name}
                                        </CollectionMobile>
                                    )
                                )}
                                <ProductName>
                                    {truncateString(
                                        capitalizeSentences(name[locale]),
                                        22,
                                    )}
                                </ProductName>
                            </MobileCardContent>
                            <CardContentDesktop>
                                <Price
                                    discountRate={discountRate}
                                    discountedPrice={useCurrency(
                                        discountedPrice,
                                    )}
                                    price={useCurrency(price)}
                                    letterSpacing="1px"
                                />
                                {product.collection_name === 'Plus Size' ? (
                                    <CollectionImage
                                        src="https://cloud.keikei.com/cdn/fervente/keikei_lovemycurves_3-removebg-preview.png"
                                        alt="plus size sticker"
                                        width="67px"
                                    />
                                ) : (
                                    product?.collection_name && (
                                        <Collection>
                                            {product.collection_name}
                                        </Collection>
                                    )
                                )}
                                <StyledRow alingItems="baseline">
                                    <ProductName>
                                        {truncateString(
                                            capitalizeSentences(name[locale]),
                                            40,
                                        )}
                                    </ProductName>
                                </StyledRow>
                            </CardContentDesktop>
                            <Variants onClick={(e) => e.preventDefault()}>
                                <ColorVariants
                                    variantColors={variants}
                                    selectedVariant={selectedVariant?.id}
                                    onProductChange={changeProduct}
                                    slug={currentSlug}
                                />
                            </Variants>
                        </CardContentWrapper>
                    )}
                </Card>
            </Link>
        </CardWrapper>
    );
};
const CardWrapper = styled.div`
    position: relative;
    width: 100%;
`;

const Card = styled.div`
    width: 100%;
    max-width: ${({ listingType }) =>
        listingType ? listingType.width : '100vw'};
    overflow: hidden;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    border-color: ${({ theme }) => theme.colors.primary};
    border-style: solid;
    border-width: 0 1px 1px 0;
    cursor: pointer;

    @media (max-width: 768px) {
        justify-content: flex-end;
        border-width: 0 1px 1px 0;
    }

    @media (min-width: 1023px) {
        border-top: ${({ border, theme }) =>
        border ? `1px solid ${theme.colors.primary}` : 'none'};
    }
`;
const Collection = styled(Subtitle2)`
    position: absolute;
    top: 0;
    right: 0;

    @media (min-width: 1024px) {
        top: 13px;
    }
`;
const CollectionImage = styled.img`
    position: absolute;
    top: 0;
    right: 0;
    @media (max-width: 1024px) {
        position: absolute;
        top: -3px;
        right: -12px;
        width: 50px;
    }
`;

const CollectionMobile = styled(Caption)`
    position: absolute;
    top: 0;
    right: 0;

    @media (min-width: 1024px) {
        top: 13px;
    }
`;
const ImageWrapper = styled.div`
    width: 100%;
    height: ${({ listingType }) => listingType && listingType.height};
    position: relative;
    overflow: hidden;
`;
const CardContentWrapper = styled.div`
    width: 100%;
    min-height: 88px;
    padding: 5px 15px 7px 15px;

    @media (max-width: 1024px) {
        padding: 5px 15px 8px 15px;
    }
`;
const Variants = styled.div`
    display: flex;
    justify-content: flex-start;
    width: fit-content;
    margin-top: 6px;
`;

const CardContentDesktop = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    bottom: 0;
    left: 0;
    width: 100%;
    text-transform: capitalize;

    p {
        max-width: calc(100% - 55px);
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    @media (max-width: 1023px) {
        display: none;
    }
`;
const ProductName = styled(Caption2)`
    margin: 4px 0 6px;
`;
const MobileCardContent = styled.div`
    z-index: 1;
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    text-transform: capitalize;

    @media (min-width: 1023px) {
        display: none;
    }
`;

export default ProductCard;
